import { useState, useCallback } from 'react'
import { getSessionStorageData, setSessionStorageData } from '@/utils/handleSessionStorage'
import { getLocalStorageData, setLocalStorageData } from '@/utils/handleLocalStorage'

import settings from '@/settings/public'

export const BANNER_STATES = {
  SHOWN: 'shown',
  DISMISSED: 'dismissed',
  UNSET: 'unset',
} as const

type BannerState = typeof BANNER_STATES[keyof typeof BANNER_STATES]

interface IBannerOptions {
  id: string
  enabled?: boolean
  version?: number
  dismissDurationDays?: number
}

interface IBannerStorageIdentity {
  _id: string
  version?: number
  dismissDurationDays?: number
}

interface IStoredBannerState {
  state: BannerState
  expiresAt?: number
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

const isBannerState = (value: unknown): value is BannerState =>
  Object.values(BANNER_STATES).includes(value as BannerState)

// Bumping `version` in the config changes the key, re-showing the banner to
// users who dismissed a previous version.
const getStorageKey = (id: string, version?: number): string =>
  version !== undefined
    ? `_${settings.app?.code}:${id}:v${version}`
    : `_${settings.app?.code}:${id}`

// Without a TTL, dismissal keeps the original per-session semantics (sessionStorage).
// With dismissDurationDays, state lives in localStorage and expires after N days.
const isPersistentBanner = (dismissDurationDays?: number): boolean =>
  typeof dismissDurationDays === 'number' && dismissDurationDays > 0

const readStoredState = (storageKey: string, isPersistent: boolean): BannerState => {
  const storedValue = isPersistent
    ? getLocalStorageData(storageKey)
    : getSessionStorageData(storageKey)
  if (!storedValue) return BANNER_STATES.UNSET

  if (isPersistent) {
    try {
      const parsed: unknown = JSON.parse(storedValue)
      const record = parsed as IStoredBannerState
      if (record?.expiresAt && Date.now() > record.expiresAt) return BANNER_STATES.UNSET
      return isBannerState(record?.state) ? record.state : BANNER_STATES.UNSET
    } catch {
      return BANNER_STATES.UNSET
    }
  }

  return isBannerState(storedValue) ? storedValue : BANNER_STATES.UNSET
}

/**
 * Storage-backed dismissal check for a banner config, honoring the versioned
 * key and the TTL record. Lets banner selection (Layout) skip dismissed
 * banners so their visibility slot goes to the next eligible banner.
 */
export const isBannerDismissed = (banner: IBannerStorageIdentity): boolean =>
  readStoredState(
    getStorageKey(banner._id, banner.version),
    isPersistentBanner(banner.dismissDurationDays)
  ) === BANNER_STATES.DISMISSED

export function useBanner(options: IBannerOptions) {
  const { id, enabled = true, version, dismissDurationDays } = options
  const isPersistent = isPersistentBanner(dismissDurationDays)
  const storageKey = getStorageKey(id, version)

  const [bannerState, setBannerState] = useState<BannerState>(() =>
    readStoredState(storageKey, isPersistent)
  )

  // Persisting synchronously at the transition (not in an effect) keeps the
  // stored expiry fixed at dismissal time and lets same-render readers
  // (banner selection in Layout) observe the change immediately.
  const persistState = useCallback((newState: BannerState) => {
    if (!id) return
    if (isPersistent) {
      const record: IStoredBannerState = { state: newState }
      if (newState === BANNER_STATES.DISMISSED) {
        record.expiresAt = Date.now() + (dismissDurationDays ?? 0) * MS_PER_DAY
      }
      setLocalStorageData(storageKey, JSON.stringify(record))
    } else {
      setSessionStorageData(storageKey, newState)
    }
  }, [id, isPersistent, dismissDurationDays, storageKey])

  const dismissBanner = useCallback(() => {
    persistState(BANNER_STATES.DISMISSED)
    setBannerState(BANNER_STATES.DISMISSED)
  }, [persistState])

  const showBanner = useCallback(() => {
    persistState(BANNER_STATES.SHOWN)
    setBannerState(BANNER_STATES.SHOWN)
  }, [persistState])

  const isVisible = (bannerState === BANNER_STATES.SHOWN) && enabled

  return {
    bannerState,
    dismissBanner,
    isVisible,
    showBanner,
  }
}
